// src/components/AccountSwitcherMenu.tsx
// X(Twitter)のような複数アカウント切替メニュー。ヘッダーのプロフィール画像から開く。
// ログインが新しい順にアカウントを一覧表示し、タップで切替。下部に別アカウントを
// 追加ログインするための「ログイン」、その下に現在のアカウントのログアウトボタンを表示する
// （他に残っているアカウントがあれば自動でそちらに切り替わる）。

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../context/AuthContext';
import { getUserProfileImageByuserSubCF } from '../utils/userProfileApi';

interface AccountSwitcherMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const AccountSwitcherMenu: React.FC<AccountSwitcherMenuProps> = ({ anchorEl, onClose }) => {
  const { userSub, loggedInAccounts, refreshLoggedInAccounts, switchAccount, logout } = useAuth();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // user.username ではなくアカウント一覧（cognito:username由来で確実に埋まっている）から引く
  const currentUsername = loggedInAccounts.find((a) => a.sub === userSub)?.username;

  const [images, setImages] = useState<Record<string, string | null>>({});
  const [switchingUsername, setSwitchingUsername] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    refreshLoggedInAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loggedInAccounts.forEach((account) => {
      if (images[account.sub] !== undefined) return;
      getUserProfileImageByuserSubCF(account.sub)
        .then((img) => setImages((prev) => ({ ...prev, [account.sub]: img || null })))
        .catch(() => setImages((prev) => ({ ...prev, [account.sub]: null })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loggedInAccounts]);

  const handleSwitch = async (username: string, sub: string) => {
    if (sub === userSub) {
      onClose();
      return;
    }
    setSwitchingUsername(username);
    const ok = await switchAccount(username);
    setSwitchingUsername(null);
    onClose();
    if (!ok) {
      window.alert(`@${username} への切り替えに失敗しました。再度ログインが必要です。`);
    }
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/');
  };

  const handleAddAccount = () => {
    onClose();
    navigate('/login');
  };

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {loggedInAccounts.map((account) => (
        <MenuItem
          key={account.sub}
          selected={account.sub === userSub}
          onClick={() => handleSwitch(account.username, account.sub)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 220 }}
        >
          {images[account.sub] ? (
            <img
              src={images[account.sub] as string}
              alt=""
              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.12)',
                flexShrink: 0,
              }}
            />
          )}
          <Typography sx={{ fontSize: 14, fontWeight: account.sub === userSub ? 700 : 400 }}>
            @{account.username}
          </Typography>
          {switchingUsername === account.username && <CircularProgress size={14} sx={{ ml: 'auto' }} />}
        </MenuItem>
      ))}
      <Divider />
      <MenuItem onClick={handleAddAccount}>ログイン</MenuItem>
      <MenuItem onClick={handleLogout}>@{currentUsername}からログアウト</MenuItem>
    </Menu>
  );
};

export default AccountSwitcherMenu;
